// Island hydration — runs in the browser after the server-rendered page loads.
// Finds [data-island] elements, imports the matching component module, and
// mounts it with the serialized props so signals become reactive.

import { createRoot } from './signal.js'

export interface IslandManifest {
  [islandId: string]: () => Promise<{ default: (props: Record<string, unknown>) => Node }>
}

export async function hydrate(manifest: IslandManifest): Promise<void> {
  const islands = document.querySelectorAll<HTMLElement>('[data-island]')

  await Promise.all(
    Array.from(islands).map(async (el) => {
      const id = el.dataset.island
      if (!id || !(id in manifest)) return

      let props: Record<string, unknown> = {}
      try {
        props = JSON.parse(el.dataset.props ?? '{}')
      } catch {
        console.warn(`[davaux] Failed to parse props for island "${id}"`)
      }

      // Capture server-rendered slot content before mounting overwrites it.
      // Components mark non-serializable children slots with data-island-slot
      // so the server HTML can be restored into the hydrated DOM tree.
      const slotSnapshots: Node[][] = []
      el.querySelectorAll('[data-island-slot]').forEach((slot) => {
        slotSnapshots.push([...slot.childNodes].map((n) => n.cloneNode(true)))
      })

      const { default: Component } = await manifest[id]()

      // Run the component inside a reactive root so any onCleanup() calls
      // (e.g. from createResource refetchInterval) are collected and can be
      // disposed if the island is ever unmounted.
      const node = createRoot(() => Component(props))

      // Replace server-rendered children with the live DOM subtree
      el.replaceChildren(node)

      // Re-inject server-rendered children into any slot elements whose client
      // render produced no meaningful content (e.g. because children was a
      // non-serializable OmlNode Promise that became {} in JSON and rendered
      // as the text "[object Object]").
      if (slotSnapshots.length > 0) {
        // children serialized as {} means a non-serializable value (Promise /
        // OmlNode) — the client can't reproduce the SSR output, so we must use
        // the captured snapshot for any marked slots.
        const c = props.children
        const childrenNonSerializable =
          c !== null &&
          c !== undefined &&
          typeof c === 'object' &&
          !Array.isArray(c) &&
          Object.keys(c as Record<string, unknown>).length === 0
        if (childrenNonSerializable) {
          let i = 0
          el.querySelectorAll('[data-island-slot]').forEach((slot) => {
            const captured = slotSnapshots[i++]
            if (captured?.length) slot.replaceChildren(...captured)
          })
        }
      }
    }),
  )
}
